/**
 * `useLabourAttendance` — every attendance record for one labourer
 * on one project, across all dates.
 *
 * Mirrors the mobile labour detail screen
 *   /Users/gudupuramesh/Documents/siteexpens/app/(app)/projects/[id]/attendance/[recordId].tsx
 * lines 158–182. Same query shape: subscribe to the project's
 * `attendance` collection (orgId + projectId rule-safe scope),
 * then filter client-side by labourId.
 *
 * We don't add a `where('labourId', '==', X)` clause because
 * Firestore would need a separate composite index for it; the
 * client-side filter is cheap (a project rarely has more than a
 * few hundred attendance docs).
 */
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import type { AttendanceRecord } from '@/lib/attendance';
import { db } from '@/lib/firebase';

export type UseLabourAttendanceResult = {
  data: AttendanceRecord[];
  loading: boolean;
};

export function useLabourAttendance(
  orgId: string | null | undefined,
  projectId: string | undefined,
  labourId: string | undefined,
): UseLabourAttendanceResult {
  const [all, setAll] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId || !projectId || !labourId) {
      setAll([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      query(
        collection(db, 'attendance'),
        where('orgId', '==', orgId),
        where('projectId', '==', projectId),
      ),
      (snap) => {
        setAll(
          snap.docs.map((d) => ({
            id: d.id,
            ...(d.data() as Omit<AttendanceRecord, 'id'>),
          })),
        );
        setLoading(false);
      },
      (err) => {
        console.warn('[useLabourAttendance] snapshot error:', err);
        setLoading(false);
      },
    );
  }, [orgId, projectId, labourId]);

  const data = useMemo(() => {
    if (!labourId) return [];
    return all
      .filter((r) => r.labourId === labourId)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [all, labourId]);

  return { data, loading };
}
